/*!
 * Copyright © 2005-2025 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Alfresco Example Content Application
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail. Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * from Hyland Software. If not, see <http://www.gnu.org/licenses/>.
 */

import { CommonModule } from '@angular/common';
import { Component, inject, OnDestroy, OnInit, signal, ViewEncapsulation } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { AssistantService } from '../assistant/assistant.service';
import { OcrPipelineService } from '../ocr/ocr-pipeline.service';
import { DmsMetadataService, DmsOption } from '../upload/dms-metadata.service';
import { docxText, isDocx } from './docx-text';
import { draftToForm, FormDraftHandover } from './forms.draft';

/** The pages of a PDF read for a draft: a form on paper is a page or two, an appendix is not the form. */
const DRAFT_PAGES = 6;
const ACCEPT = '.pdf,.docx,.txt,.png,.jpg,.jpeg,.bmp,.gif,.webp';

/**
 * Pick an existing form — a photo or scan of the paper, a PDF or a Word file —
 * and have the AI draft it as a form. The file's text is read here, in the
 * browser; only that text goes to the model, and only if the confidentiality
 * chosen for it lets it go to the model in use. Nothing is saved: the draft
 * opens in the builder for the administrator to check.
 */
@Component({
  selector: 'app-form-draft-dialog',
  standalone: true,
  imports: [CommonModule, FormsModule, MatDialogModule, MatIconModule, MatProgressBarModule, TranslatePipe],
  encapsulation: ViewEncapsulation.None,
  template: `
    <div class="aca-frmdraft">
      <h2 mat-dialog-title class="aca-frmdraft__title">
        <mat-icon class="aca-frmdraft__spark">auto_awesome</mat-icon>{{ 'DMS.FORMS.DRAFT_TITLE' | translate }}
      </h2>
      <mat-dialog-content>
        <p class="aca-frmdraft__intro">{{ 'DMS.FORMS.DRAFT_INTRO' | translate }}</p>

        <label class="aca-frmdraft__file" [class.aca-frmdraft__file--on]="!!file()">
          <input type="file" [accept]="accept" (change)="pick($any($event.target).files)" [disabled]="busy()" />
          <mat-icon>{{ file() ? 'description' : 'upload_file' }}</mat-icon>
          <span>{{ file()?.name || ('DMS.FORMS.DRAFT_PICK' | translate) }}</span>
        </label>
        <p class="aca-frmdraft__hint">{{ 'DMS.FORMS.DRAFT_KINDS' | translate }}</p>

        <ng-container *ngIf="levels().length">
          <label class="aca-frmdraft__level">
            <span>{{ 'DMS.FORMS.DRAFT_LEVEL' | translate }}</span>
            <select [ngModel]="level()" (ngModelChange)="level.set($event)" [disabled]="busy()">
              <option *ngFor="let option of levels()" [value]="option.value">{{ option.label | translate }}</option>
            </select>
          </label>
          <p class="aca-frmdraft__hint">{{ 'DMS.FORMS.DRAFT_LEVEL_HINT' | translate }}</p>
        </ng-container>

        <div class="aca-frmdraft__step" *ngIf="busy()" role="status">
          <mat-progress-bar mode="indeterminate" />
          <span>{{ (step() === 'reading' ? 'DMS.FORMS.DRAFT_READING' : 'DMS.FORMS.DRAFT_ASKING') | translate: { model: modelName } }}</span>
        </div>
        <p class="aca-frmdraft__error" *ngIf="problem()" role="alert"><mat-icon>error_outline</mat-icon>{{ problem() }}</p>
      </mat-dialog-content>
      <mat-dialog-actions align="end">
        <button type="button" class="aca-frmdraft__outline" (click)="close()">{{ 'DMS.UI.7D3094' | translate }}</button>
        <button type="button" class="aca-frmdraft__primary" (click)="draft()" [disabled]="!file() || busy()">
          <mat-icon>auto_awesome</mat-icon>{{ 'DMS.FORMS.DRAFT_GO' | translate }}
        </button>
      </mat-dialog-actions>
    </div>
  `
})
export class FormDraftDialogComponent implements OnInit, OnDestroy {
  private readonly ref = inject<MatDialogRef<FormDraftDialogComponent, FormDraftHandover>>(MatDialogRef);
  private readonly assistant = inject(AssistantService);
  private readonly pipeline = inject(OcrPipelineService);
  private readonly metadata = inject(DmsMetadataService);
  private readonly translate = inject(TranslateService);

  readonly accept = ACCEPT;
  readonly file = signal<File | null>(null);
  /** The source's confidentiality: whether a cloud model may read it is the assistant's rule, as for any document. */
  readonly level = signal('internal');
  readonly levels = signal<DmsOption[]>([]);
  readonly step = signal<'reading' | 'asking' | null>(null);
  readonly problem = signal('');
  readonly busy = () => this.step() !== null;

  private asking?: Subscription;
  private closed = false;

  get modelName(): string {
    return this.assistant.status()?.provider?.model ?? '';
  }

  ngOnInit(): void {
    this.metadata.schema().subscribe({
      next: (schema) => this.levels.set(schema.fields['dms:confidentiality']?.options ?? []),
      error: () => undefined
    });
  }

  ngOnDestroy(): void {
    this.closed = true;
    this.asking?.unsubscribe();
  }

  pick(files: FileList | null): void {
    this.file.set(files?.[0] ?? null);
    this.problem.set('');
  }

  close(): void {
    this.ref.close();
  }

  async draft(): Promise<void> {
    const file = this.file();
    if (!file || this.busy()) {
      return;
    }
    this.problem.set('');
    this.step.set('reading');

    let text = '';
    try {
      text = await this.textOf(file);
    } catch {
      this.fail(isDocx(file.name) ? 'DMS.FORMS.DRAFT_BAD_WORD' : 'DMS.FORMS.DRAFT_READ_FAILED');
      return;
    }
    if (this.closed) {
      return;
    }
    if (!text.trim()) {
      this.fail('DMS.FORMS.DRAFT_NO_TEXT');
      return;
    }

    this.step.set('asking');
    this.asking = this.assistant.draftForm(file.name, text, this.level()).subscribe({
      next: (result) => {
        if (result.draft) {
          this.ref.close({ form: draftToForm(result.draft), source: file.name, model: result.model });
          return;
        }
        this.fail(
          result.reason === 'confidentiality'
            ? 'DMS.FORMS.DRAFT_CONFIDENTIAL'
            : result.reason === 'no-text'
              ? 'DMS.FORMS.DRAFT_NO_TEXT'
              : 'DMS.FORMS.DRAFT_NOTHING'
        );
      },
      error: (error) => {
        if (error?.status === 403) {
          this.fail('DMS.FORMS.DRAFT_OFF');
        } else {
          this.fail('DMS.FORMS.DRAFT_FAILED', error?.error?.message ?? error?.message ?? '');
        }
      }
    });
  }

  /** A Word file is unpacked here; a PDF is read from its text layer or, page by page, by OCR; a photo by OCR. */
  private textOf(file: File): Promise<string> {
    return isDocx(file.name) ? docxText(file) : this.pipeline.textOf(file, file.name, DRAFT_PAGES);
  }

  private fail(key: string, detail = ''): void {
    this.step.set(null);
    this.problem.set(this.translate.instant(key, { detail }));
  }
}
